import React from 'react';
import dayjs from 'dayjs';
import { Button } from '../../../../Components/MUIs';
import { Box, IconButton } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

/**
 * PPE_BASIC_02 (보호구 관리대장) 컬럼 정의
 * - 기존 `PPE_BASIC_02.js`의 `columnDefs1`를 분리
 * - 그룹행/하위행 렌더링(펼침/접힘, 내역추가 버튼 등) 포함
 */
export const getColumnDefs1 = (props = {}) => {
  return [
    {
      field: 'group',
      headerName: '보호구명',
      flex: 1,
      minWidth: 200,
      editable: false,
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return {
            backgroundColor: '#e3f2fd',
            fontWeight: 'bold',
            paddingLeft: '8px',
            paddingTop: '8px',
            paddingBottom: '8px',
          };
        }
        return {
          paddingLeft: '24px',
        };
      },
      cellRenderer: (params) => {
        if (params.data?.isGroupRow) {
          const isExpanded = params.data?.isExpanded !== false; // 기본값 true
          const rowData = props.rowData1 || [];
          const currentErCd = params.data?.erCd;

          // 현재 그룹의 하위 행이 있는지 확인
          const hasSubRows = rowData.some(
            (row) => !row.isGroupRow && row.groupErCd === currentErCd
          );

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  props.handleToggleGroup?.(params.data);
                }}
                sx={{ padding: '4px', marginRight: '4px' }}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
                  <span>{params.value}</span>
                  {/* 하위 행이 없고 showAddSubButton이 있으면 보호구명 옆에 버튼 표시 */}
              {!hasSubRows && params.data?.showAddSubButton && (
                <Button
                  size="small"
                  name={'+ 내역 추가'}
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.handleAddSubItem?.(params.data);
                  }}
                  sx={{ ml: 1, color: '#2EB450', whiteSpace: 'nowrap' }}
                ></Button>
              )}
            </Box>
          );
        } else {
          // 하위 행인 경우
          const rowData = props.rowData1 || [];
          const currentGroupErCd = params.data?.groupErCd;

          // 마지막 하위 행이면 '+내역추가' 버튼 표시
          if (params.data?.isLastSubRow && currentGroupErCd) {
            const groupRow = rowData.find(
              (row) => row.isGroupRow && row.erCd === currentGroupErCd
            );
            if (groupRow) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size="small"
                    name={'+ 내역 추가'}
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.handleAddSubItem?.(groupRow);
                    }}
                    sx={{ color: '#2EB450', whiteSpace: 'nowrap' }}
                  ></Button>
                </Box>
              );
            }
          }

          // 일반 하위 행은 빈 값 반환
          return '';
        }
      },
    },
    {
      field: 'gubun',
      headerName: '구분',
      width: 150,
      editable: (params) =>
        !params.data?.isGroupRow && params.data?.newYn === 'Y',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['입고', '출고'],
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        if (params.data?.newYn !== 'Y') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
          };
        }
        return {};
      },
    },
    {
      field: 'unit',
      headerName: '단위',
      width: 100,
      editable: false, // 단위는 수정 불가
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['EA', 'SET', '기타'],
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
        };
      },
    },
    {
      field: 'spec',
      headerName: '규격',
      width: 200,
      editable: false, // 규격은 수정 불가
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
        };
      },
    },
    {
      field: 'paymentStandard',
      headerName: '지급기준(사용기한)',
      width: 200,
      editable: false, // 지급기준은 수정 불가
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'center' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
          textAlign: 'center',
        };
      },
    },
    {
      field: 'price',
      headerName: '단가(원)',
      width: 120,
      editable: false, // 단가는 수정 불가
      cellEditor: 'agNumberCellEditor',
      cellRenderer: props.NumberCommaRenderer,
      cellEditorParams: {
        onCellEditorCreated: (params) => {
          const input = document.querySelector('input.ag-input-field-input');
          if (input) {
            input.addEventListener('input', () => {
              let val = input.value.replace(/[^0-9]/g, '');
              if (val.length > 10 || parseInt(val) > 2147483647) {
                val = '2147483647';
              }
              input.value = val;
            });
          }
        },
      },
      valueParser: (params) => {
        const raw = params.newValue?.toString().replace(/[^0-9]/g, '') || '';
        let parsed = parseInt(raw, 10);
        return isNaN(parsed) ? null : Math.min(parsed, 2147483647);
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'right' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
          textAlign: 'right',
        };
      },
    },
    {
      field: 'inDate',
      headerName: '입고일자',
      width: 130,
      valueGetter: (params) => {
        // 구분이 "입고"일 때만 moveDate 표시
        if (params.data?.gubun === '입고') {
          return params.data?.moveDate || params.data?.inDate;
        }
        return null;
      },
      editable: (params) => {
        if (params.data?.isGroupRow) return false;
        if (params.data?.newYn !== 'Y') return false;
        // 구분이 "입고"일 때만 편집 가능
        return params.data?.gubun === '입고';
      },
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        format: 'yyyy-MM-dd',
      },
      valueFormatter: (params) => {
        if (!params.value) return '';
        return dayjs(params.value).format('YYYY-MM-DD');
      },
      valueSetter: (params) => {
        // 편집 시 moveDate에 저장
        if (params.data) {
          params.data.moveDate = params.newValue;
          params.data.inDate = params.newValue;
        }
        return true;
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'center' };
        }
        if (params.data?.newYn !== 'Y' || params.data?.gubun !== '입고') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
            textAlign: 'center',
          };
        }
        return { textAlign: 'center' };
      },
    },
    {
      field: 'inQty',
      headerName: '입고수량',
      width: 120,
      editable: (params) => {
        if (params.data?.isGroupRow) return false;
        if (params.data?.newYn !== 'Y') return false;
        // 구분이 "입고"일 때만 편집 가능
        return params.data?.gubun === '입고';
      },
      cellEditor: 'agNumberCellEditor',
      cellRenderer: props.NumberCommaRenderer,
      cellEditorParams: {
        onCellEditorCreated: (params) => {
          const input = document.querySelector('input.ag-input-field-input');
          if (input) {
            input.addEventListener('input', () => {
              let val = input.value.replace(/[^0-9]/g, '');
              if (val.length > 10 || parseInt(val) > 2147483647) {
                val = '2147483647';
              }
              input.value = val;
            });
          }
        },
      },
      valueParser: (params) => {
        const raw = params.newValue?.toString().replace(/[^0-9]/g, '') || '';
        let parsed = parseInt(raw, 10);
        return isNaN(parsed) ? null : Math.min(parsed, 2147483647);
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'right' };
        }
        if (params.data?.newYn !== 'Y' || params.data?.gubun !== '입고') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
            textAlign: 'right',
          };
        }
        return { textAlign: 'right' };
      },
    },
    {
      field: 'outDate',
      headerName: '출고일자',
      width: 130,
      valueGetter: (params) => {
        // 구분이 "출고"일 때만 moveDate 표시
        if (params.data?.gubun === '출고') {
          return params.data?.moveDate || params.data?.outDate;
        }
        return null;
      },
      editable: (params) => {
        if (params.data?.isGroupRow) return false;
        if (params.data?.newYn !== 'Y') return false;
        // 구분이 "출고"일 때만 편집 가능
        return params.data?.gubun === '출고';
      },
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        format: 'yyyy-MM-dd',
      },
      valueFormatter: (params) => {
        if (!params.value) return '';
        return dayjs(params.value).format('YYYY-MM-DD');
      },
      valueSetter: (params) => {
        // 편집 시 moveDate에 저장
        if (params.data) {
          params.data.moveDate = params.newValue;
          params.data.outDate = params.newValue;
        }
        return true;
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'center' };
        }
        if (params.data?.newYn !== 'Y' || params.data?.gubun !== '출고') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
            textAlign: 'center',
          };
        }
        return { textAlign: 'center' };
      },
    },
    {
      field: 'outQty',
      headerName: '출고수량',
      width: 120,
      editable: (params) => {
        if (params.data?.isGroupRow) return false;
        if (params.data?.newYn !== 'Y') return false;
        // 구분이 "출고"일 때만 편집 가능
        return params.data?.gubun === '출고';
      },
      cellEditor: 'agNumberCellEditor',
      cellRenderer: props.NumberCommaRenderer,
      cellEditorParams: {
        onCellEditorCreated: (params) => {
          const input = document.querySelector('input.ag-input-field-input');
          if (input) {
            input.addEventListener('input', () => {
              let val = input.value.replace(/[^0-9]/g, '');
              if (val.length > 10 || parseInt(val) > 2147483647) {
                val = '2147483647';
              }
              input.value = val;
            });
          }
        },
      },
      valueParser: (params) => {
        const raw = params.newValue?.toString().replace(/[^0-9]/g, '') || '';
        let parsed = parseInt(raw, 10);
        return isNaN(parsed) ? null : Math.min(parsed, 2147483647);
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'right' };
        }
        if (params.data?.newYn !== 'Y' || params.data?.gubun !== '출고') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
            textAlign: 'right',
          };
        }
        return { textAlign: 'right' };
      },
    },
    {
      field: 'currentQty',
      headerName: '현재 재고수량',
      width: 130,
      editable: false,
      cellRenderer: props.NumberCommaRenderer,
      valueFormatter: (params) => {
        // 그룹 행과 하위 행 모두 현재 재고수량 표시
        return params.value != null ? params.value : '';
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd', textAlign: 'right' };
        }
        return { textAlign: 'right', backgroundColor: '#f5f5f5' };
      },
    },
    {
      field: 'reason',
      headerName: '사유',
      width: 170,
      editable: (params) =>
        !params.data?.isGroupRow && params.data?.newYn === 'Y',
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        if (params.data?.newYn !== 'Y') {
          return {
            backgroundColor: '#f5f5f5',
            color: '#666',
            fontStyle: 'italic',
            cursor: 'not-allowed',
          };
        }
        return {};
      },
    },
    {
      field: 'depNmLast',
      headerName: '담당부서',
      width: 150,
      editable: false, // 담당부서는 수정 불가
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
        };
      },
    },
    {
      field: 'managerName',
      headerName: '담당자',
      width: 120,
      editable: false, // 담당자는 수정 불가
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
        };
      },
    },
    {
      field: 'remark',
      headerName: '비고',
      flex: 1,
      editable: false, // 비고는 수정 불가
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: (params) => {
        if (params.data?.isGroupRow) {
          return { backgroundColor: '#e3f2fd' };
        }
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          fontStyle: 'italic',
          cursor: 'not-allowed',
        };
      },
    },
  ];
};

export default getColumnDefs1;
